import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useClosing } from '../lib/overlay';
import { CommandPaletteDialog } from './CommandPaletteDialog';

// The command palette (⌘K / Ctrl K) — every screen, repo and recent session
// reachable by typing. Hand-rolled on the app's own overlay seams (`useClosing`
// + `useDialogTrap`), not a `cmdk` dependency: the dashboard's design system is
// self-contained SCSS/BEM (`docs/idea/ui.md`) and a headless component library
// would arrive with its own DOM, its own focus model and its own classes.
//
// This shell owns exactly one thing — the exit animation — so that the guts,
// which hold two queries, mount only while the palette is on screen. Everything
// else lives in <CommandPaletteDialog/>.

/** Exit animation length. Must match `.palette--closing` in `styles/ui/_palette.scss`. */
export const PALETTE_EXIT_MS = 120;

export function CommandPalette(props: {
  readonly open: boolean;
  /** The admin radar offers its own destinations and no org-scoped rows (#754). */
  readonly adminHost: boolean;
  /** The shell's org-aware href resolver — one seam, shared with the sidebar. */
  readonly resolve: (href: string) => string;
  readonly onClose: () => void;
}): JSX.Element {
  const gate = useClosing(() => props.open, PALETTE_EXIT_MS);
  return (
    <Show when={gate.visible()}>
      <div class="palette" classList={{ 'palette--closing': gate.closing() }}>
        <button
          type="button"
          class="palette__overlay"
          tabindex="-1"
          aria-label={t('modal.dismiss')}
          onClick={props.onClose}
        />
        <CommandPaletteDialog
          open={props.open}
          adminHost={props.adminHost}
          resolve={props.resolve}
          onClose={props.onClose}
        />
      </div>
    </Show>
  );
}
